import { Monitor, Zap, Clock, PenLine } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ScrollReveal } from '@/hooks/useScrollReveal';

const FEATURES = [
  { icon: PenLine, title: 'Edit Instantly', desc: 'Change text, images, and content yourself — live on your site in seconds.' },
  { icon: Clock, title: 'No Waiting', desc: 'No emails, no tickets, no week-long delays for a simple text change.' },
  { icon: Zap, title: 'Always Included', desc: 'Free with every care plan. Your site, your control, from day one.' },
  { icon: Monitor, title: 'Built for You', desc: 'A clean, simple dashboard designed for non-technical business owners.' },
];

export default function AdminPanelUSP() {
  return (
    <section data-testid="admin-panel-usp" className="py-24 md:py-32 bg-[#0A0A0A] relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '32px 32px' }} />
      <div className="absolute top-20 left-0 w-[400px] h-[400px] bg-[#00E5FF]/5 rounded-full blur-[120px]" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left — Text */}
          <div>
            <ScrollReveal>
              <span className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-[#00E5FF] block mb-4">
                Your Website, Your Control
              </span>
              <h2 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-6">
                Update Your Site Yourself — In Seconds
              </h2>
              <p className="text-base sm:text-lg leading-relaxed text-slate-400 mb-8">
                Every website we build comes with our custom admin panel. Change your wording, swap images, update prices — all without waiting for us. Not happy with something after launch? Fix it yourself, free, as long as you're hosted with us.
              </p>
              <p className="text-sm text-slate-500 mb-8">
                It's the difference between waiting days for a small change and doing it yourself in 30 seconds. Your business moves fast — your website should keep up.
              </p>
              <Link
                to="/pricing"
                data-testid="admin-usp-cta"
                className="inline-flex items-center gap-2 bg-[#00E5FF] text-[#0A0A0A] px-7 py-3.5 rounded-full text-sm font-semibold hover:bg-white transition-all"
              >
                View Care Plans
              </Link>
              <Link
                to="/admin-demo"
                data-testid="admin-usp-demo-cta"
                target="_blank"
                rel="noopener"
                className="inline-flex items-center gap-2 ml-3 border border-white/15 text-white px-7 py-3.5 rounded-full text-sm font-semibold hover:border-[#00E5FF]/40 hover:text-[#00E5FF] transition-all"
              >
                See live demo →
              </Link>
            </ScrollReveal>
          </div>

          {/* Right — Feature cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {FEATURES.map(({ icon: Icon, title, desc }, i) => (
              <ScrollReveal key={title} delay={i * 80}>
                <div className="p-6 rounded-2xl border border-white/10 bg-white/[0.03] hover:border-[#00E5FF]/20 transition-colors">
                  <div className="w-10 h-10 rounded-xl bg-[#00E5FF]/10 flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-[#00E5FF]" />
                  </div>
                  <h3 className="font-['Outfit'] text-base font-semibold text-white mb-1">{title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
